import { useEffect, useState } from "react";
import { snapshot, useStore, applyRemote, getState } from "./store";
import { apiMe, apiLogin, apiSignup, apiLogout, apiPortalProject, type Account } from "./authApi";
import { startSync } from "./sync";
import type { Capability, RoleMatrix, RoleKey } from "./permissions";

/* Studio & team members authenticate with a real account (email + password,
   server-validated, HttpOnly session cookie). Clients open their portal with a
   per-project access code. can()/isAdmin read the editable role matrix. */

export interface Session {
  role: "studio" | "team" | "client";
  name: string;
  email?: string;
  teamRole?: RoleKey;    // account role for studio/team
  projectId?: string;    // client → their project
  code?: string;
}

const CKEY = "atelier.client.v1";   // client (code) session only — accounts use the cookie
let session: Session | null = loadClient();
let ready = false;
const subs = new Set<() => void>();
function notify() { subs.forEach((f) => f()); }
function setSession(s: Session | null) { session = s; notify(); void startSync(s); }

function loadClient(): Session | null {
  try { const raw = localStorage.getItem(CKEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function saveClient(s: Session | null) {
  try { s ? localStorage.setItem(CKEY, JSON.stringify(s)) : localStorage.removeItem(CKEY); } catch {}
}

function accountToSession(a: Account): Session {
  return a.role === "admin"
    ? { role: "studio", teamRole: "admin", name: a.name, email: a.email }
    : { role: "team", teamRole: a.role, name: a.name, email: a.email };
}

/** On app start: prefer a real account session (cookie); else a saved client session. */
export async function bootstrapAuth() {
  const acc = await apiMe();
  if (acc) { session = accountToSession(acc); saveClient(null); } // account wins; drop any stale client session
  ready = true;
  notify();
  void startSync(session); // hydrate from the server for account OR loaded client session
}

export async function signup(email: string, password: string, name: string, invite?: string) {
  const a = await apiSignup(email, password, name, invite);
  saveClient(null); setSession(accountToSession(a));
  return a;
}
export async function login(email: string, password: string) {
  const a = await apiLogin(email, password);
  saveClient(null); setSession(accountToSession(a));
  return a;
}
export async function logout() {
  try { await apiLogout(); } catch {}
  saveClient(null); setSession(null);
}

/** Client portal access — resolve a per-project access code to a scoped session.
 *
 *  The server is the source of truth: a client arrives with an empty device, so
 *  the only projects in the local store are the seeded demo ones. Matching
 *  against those would (and did) reject every real code. We fall back to the
 *  local store only so the seeded demo still opens with no worker behind it. */
export async function loginWithCode(raw: string): Promise<Session | null> {
  const code = raw.trim().toUpperCase();
  if (!code) return null;
  const remote = await apiPortalProject(code);
  const proj = remote?.project ?? snapshot().projects.find((p) => p.client.code.toUpperCase() === code) ?? null;
  if (!proj) return null;
  // Put the project — and the studio's rate card, which prices it — in the
  // store before we navigate, so the portal renders the right numbers without
  // waiting on the sync round-trip.
  if (remote) applyRemote({ ...getState(), projects: [remote.project], ...(remote.rateCard ? { rateCard: remote.rateCard } : {}) });
  const s: Session = { role: "client", name: proj.client.name, code, projectId: proj.id };
  saveClient(s); setSession(s);
  return s;
}

export function useSession(): Session | null {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((n) => n + 1); subs.add(f); return () => { subs.delete(f); }; }, []);
  return session;
}
export function getSession() { return session; }
export function useAuthReady(): boolean {
  const [r, setR] = useState(ready);
  useEffect(() => { const f = () => setR(ready); subs.add(f); return () => { subs.delete(f); }; }, []);
  return r;
}

/* ---- permissions ---- */
export function can(s: Session | null, cap: Capability, matrix: RoleMatrix): boolean {
  if (!s) return false;
  if (s.role === "studio") return true;
  if (s.role === "team") return !!matrix?.[s.teamRole ?? "viewer"]?.[cap];
  return false;
}
export function useCan(cap: Capability): boolean {
  const s = useSession();
  const { roleMatrix } = useStore();
  return can(s, cap, roleMatrix);
}
export function isAdmin(s: Session | null): boolean {
  return !!s && (s.role === "studio" || (s.role === "team" && s.teamRole === "admin"));
}
export function useIsAdmin(): boolean { return isAdmin(useSession()); }

export const ROLE_LABEL: Record<string, string> = {
  studio: "Admin", client: "Client",
  admin: "Admin", assistant: "Admin assistant", lead: "Studio lead", designer: "Designer", accountant: "Accountant", viewer: "Viewer",
};
